/*  Shared  */
import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useCallback, useEffect, useState } from 'react';

const API_PATH = '/globalTag';

/**
 * A React hook to fetch global tags from the API.
 * @param {Object} params - Query parameters for the API request.
 * @returns {Object} An object containing the global tags, loading state, and a function to refetch data.
 */
export const useFetchGlobalTags = (params) => {
    const [globalTags, setGlobalTags] = useState([]);
    const [loading, setLoading] = useState(true);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => setShouldRefetch({});

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(API_PATH, {
                    cancelToken: source.token,
                    params
                });
                if (!Response.hasData(response)) {
                    debug.w('No global tags found.', response);
                } else {
                    const data = Response.getData(response);

                    setGlobalTags(data);
                }
            } catch (error) {
                debug.e('Error getting global tags.', error);
            }
            setLoading(false);
        };
        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [shouldRefetchData, params]);

    return {
        globalTags,
        loading,
        refetchData
    };
};

/**
 * A React hook to load global tags as needed to the API.
 * @returns {Object} An object containing the get function and a loading state.
 */
export const useFetchGlobalTagsAsync = () => {
    const [loading, setLoading] = useState(false);

    const getGlobalTags = useCallback(async (params) => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        try {
            setLoading(true);
            const response = await axios.get('/globalTag', {
                cancelToken: source.token,
                params
            });
            if (!Response.hasData(response)) {
                debug.w('No global tags found.', response);
            }

            const data = Response.getData(response);

            setLoading(false);
            return data;
        } catch (err) {
            debug.e('Error getting global tags.', err);

            setLoading(false);
            return [];
        }
    }, []);

    return {
        getGlobalTags,
        loading
    };
};

/**
 * A React hook to post a new global tag to the API.
 * @returns {Object} An object containing the post function and a saving state.
 */
export const usePostGlobalTag = () => {
    const [saving, setSaving] = useState(false);

    const postGlobalTag = useCallback(async (payload) => {
        try {
            setSaving(true);

            const response = await axios.post(API_PATH, payload);

            if (!Response.hasData(response)) {
                throw new Error('No GlobalTag Response');
            }

            setSaving(false);

            return true;
        } catch (err) {
            debug.e('Error creating a GlobalTag', err);

            setSaving(false);
            return false; // success = false
        }
    }, []);

    return { postGlobalTag, saving };
};

/**
 * A React hook to update an existing global tag in the API.
 * @returns {Object} An object containing the put function and a saving state.
 */
export const usePutGlobalTag = () => {
    const [saving, setSaving] = useState(false);

    const putGlobalTag = useCallback(async (payload) => {
        try {
            setSaving(true);

            const response = await axios.put(
                `${API_PATH}/${payload.id}`,
                payload
            );

            if (!Response.hasData(response)) {
                throw new Error('No GlobalTag Response');
            }

            setSaving(false);

            return true;
        } catch (err) {
            debug.e('Error updating a GlobalTag', err);

            setSaving(false);
            return false; // success = false
        }
    }, []);

    return { putGlobalTag, saving };
};

/**
 * A React hook to delete a global tag from the API.
 * @returns {Object} An object containing the delete function and a deleting state.
 */
export const useDeleteGlobalTag = () => {
    const [deleting, setDeleting] = useState(false);

    const deleteGlobalTag = useCallback(async (payload) => {
        try {
            setDeleting(true);

            const response = await axios.delete(`${API_PATH}/${payload.id}`);

            if (!Response.hasData(response)) {
                throw new Error('No GlobalTag Response');
            }

            setDeleting(false);

            return true;
        } catch (err) {
            debug.e('Error deleting a GlobalTag', err);

            setDeleting(false);
            return false; // success = false
        }
    }, []);

    return { deleteGlobalTag, deleting };
};
